import React, { useEffect, useState } from "react";
import { ShoppingBag } from "lucide-react";

const C = {
  bg: "#3B0D0D", surf: "#FFFFFF", surf2: "#FFFCF7",
  crimson: "#8B0000", gold: "#B8860B", text: "#3B2722",
  muted: "#A78F7F", border: "#E9DED0", success: "#4F7A52",
};

const N = (n) => "₦" + Number(n || 0).toLocaleString("en-NG",
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const EMPTY = {
  items: [], subtotal: 0, discount: 0, service: 0, vat: 0, total: 0,
  order_no: "", branch: "", cashier: "", ts: 0,
};

export default function CustomerDisplay() {
  const [state, setState] = useState(() => {
    try { return JSON.parse(localStorage.getItem("pos_customer_display") || "null") || EMPTY; }
    catch { return EMPTY; }
  });

  useEffect(() => {
    const bc = "BroadcastChannel" in window ? new BroadcastChannel("pos_customer_display") : null;
    const onMsg = (e) => { setState(e.data || EMPTY); try { localStorage.setItem("pos_customer_display", JSON.stringify(e.data || EMPTY)); } catch {} };
    if (bc) bc.onmessage = onMsg;
    const onStorage = (e) => { if (e.key === "pos_customer_display") { try { setState(JSON.parse(e.newValue) || EMPTY); } catch { setState(EMPTY); } } };
    window.addEventListener("storage", onStorage);
    return () => { if (bc) bc.close(); window.removeEventListener("storage", onStorage); };
  }, []);

  const empty = !state.items || state.items.length === 0;

  return (
    <div data-testid="customer-display" style={{
      minHeight: "100vh", background: C.bg, color: "#fff",
      fontFamily: 'Inter, system-ui, sans-serif', display: "flex", flexDirection: "column",
    }}>
      <div style={{
        padding: "22px 40px", display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: "1px solid rgba(255,255,255,.09)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <img src="/logo.jpg" alt="Sir Monty" style={{ height: 54, borderRadius: 10 }} />
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: ".01em" }}>Sir Monty</div>
            <div style={{ fontSize: 13, color: "#E7D0AD", opacity: .9 }}>{state.branch || "Welcome"}</div>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, color: "#E7D0AD", letterSpacing: ".18em" }}>ORDER</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#fff" }}>{state.order_no || "—"}</div>
        </div>
      </div>

      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 30, padding: "30px 40px" }}>
        <div style={{ background: "rgba(255,255,255,.05)", borderRadius: 20, padding: 26, overflowY: "auto", maxHeight: "calc(100vh - 220px)" }}>
          <div style={{ fontSize: 12, letterSpacing: ".2em", color: "#E7D0AD", marginBottom: 18 }}>YOUR ORDER</div>
          {empty ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 0", opacity: .7 }} data-testid="customer-display-empty">
              <ShoppingBag size={64} color="#E7D0AD" />
              <div style={{ fontSize: 22, marginTop: 14, fontWeight: 600 }}>Your basket is empty</div>
              <div style={{ fontSize: 13, marginTop: 6, color: "#E7D0AD" }}>The cashier will add items in a moment.</div>
            </div>
          ) : (
            state.items.map((i, idx) => (
              <div key={idx} data-testid={`cd-line-${idx}`} style={{
                display: "flex", justifyContent: "space-between", padding: "14px 0",
                borderBottom: "1px dashed rgba(255,255,255,.14)", fontSize: 17,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700 }}>{i.quantity} × {i.name}</div>
                  {i.size && <div style={{ fontSize: 12, color: "#E7D0AD" }}>{i.size}</div>}
                  {(i.modifiers || []).map((m) => (
                    <div key={m.name} style={{ fontSize: 12, color: "#E7D0AD" }}>• {m.name}</div>
                  ))}
                </div>
                <div style={{ fontWeight: 700, marginLeft: 20, whiteSpace: "nowrap" }}>
                  {N((i.unit_price || 0) * i.quantity)}
                </div>
              </div>
            ))
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", gap: 12 }}>
          <Row label="Subtotal" value={N(state.subtotal)} />
          {state.discount > 0 && <Row label="Discount" value={"−" + N(state.discount)} color="#F5B841" />}
          {state.service > 0 && <Row label="Service" value={N(state.service)} />}
          <Row label="VAT" value={N(state.vat)} />
          <div style={{
            marginTop: 12, padding: "24px 28px", background: "#fff", color: C.crimson,
            borderRadius: 18, display: "flex", justifyContent: "space-between", alignItems: "baseline",
          }}>
            <div style={{ fontSize: 14, letterSpacing: ".2em", color: C.muted, fontWeight: 700 }}>TOTAL</div>
            <div data-testid="cd-total" style={{ fontSize: 46, fontWeight: 900 }}>{N(state.total)}</div>
          </div>
          <div style={{ fontSize: 12, textAlign: "center", color: "#E7D0AD", marginTop: 8 }}>
            Thank you for choosing Sir Monty
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, color }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", padding: "10px 4px",
      fontSize: 18, color: color || "#fff",
    }}>
      <span style={{ opacity: .8 }}>{label}</span>
      <span style={{ fontWeight: 700 }}>{value}</span>
    </div>
  );
}
